// „Neuer Dienstleister“ (Handoff Dienstleister §8): Entwurf, Prüfung, Schritte, Bevorzugt/Vertretung, Payload. Reine Funktionen.

import { emailGueltig } from '../immobilien/portal';

export type DlArt = 'firma' | 'einzel' | 'behoerde';
export const DL_ARTEN: readonly { key: DlArt; label: string }[] = [
  { key: 'firma', label: 'Firma' }, { key: 'einzel', label: 'Einzelunternehmen' }, { key: 'behoerde', label: 'Behörde / Versorger' },
];
export type Einordnung = 'bevorzugt' | 'vertretung';

export interface NeuerDlEntwurf {
  art: DlArt;
  name: string; ansprechpartner: string; email: string; telefon: string; notdienstTelefon: string; website: string;
  strasse: string; hausnummer: string; plz: string; stadt: string;
  kategorieIds: string[];
  einordnung: Einordnung;
  stundensatz: string; anfahrt: string; notdienstzuschlag: string; zahlungsziel: string;
  iban: string; ustId: string; freistellungBis: string; haftpflichtBis: string; notizen: string;
  freigabegrenze: number;
  modus: 'direkt' | 'angebot';
  notfall: boolean;
}

export const LEERER_DL_ENTWURF: NeuerDlEntwurf = {
  art: 'firma', name: '', ansprechpartner: '', email: '', telefon: '', notdienstTelefon: '', website: '',
  strasse: '', hausnummer: '', plz: '', stadt: '', kategorieIds: [], einordnung: 'vertretung',
  stundensatz: '', anfahrt: '', notdienstzuschlag: '', zahlungsziel: '', iban: '', ustId: '', freistellungBis: '', haftpflichtBis: '', notizen: '',
  freigabegrenze: 500, modus: 'angebot', notfall: false,
};

/** „55,50“ → 55.5; leer → null; ungültig → NaN. */
export function zahl(roh: string): number | null {
  const t = roh.trim().replace(/\s/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  if (!t) return null;
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : Number.NaN;
}

const IBAN = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
export const ibanNorm = (s: string) => s.replace(/\s/g, '').toUpperCase();

/** IBAN-Prüfsumme (ISO 13616, mod 97). */
export function ibanGueltig(roh: string): boolean {
  const iban = ibanNorm(roh);
  if (!IBAN.test(iban)) return false;
  const umgestellt = `${iban.slice(4)}${iban.slice(0, 4)}`.replace(/[A-Z]/g, c => String(c.charCodeAt(0) - 55));
  let rest = 0;
  for (const ziffer of umgestellt) rest = (rest * 10 + Number(ziffer)) % 97;
  return rest === 1;
}

export function feldFehler(e: NeuerDlEntwurf): Partial<Record<keyof NeuerDlEntwurf, string>> {
  const f: Partial<Record<keyof NeuerDlEntwurf, string>> = {};
  if (e.email.trim() && !emailGueltig(e.email)) f.email = 'E-Mail prüfen';
  if (e.plz.trim() && !/^\d{5}$/.test(e.plz.trim())) f.plz = '5 Ziffern';
  if (e.iban.trim() && !ibanGueltig(e.iban)) f.iban = 'IBAN prüfen';
  for (const k of ['stundensatz', 'anfahrt', 'notdienstzuschlag', 'zahlungsziel'] as const) {
    const z = zahl(e[k]);
    if (z !== null && (Number.isNaN(z) || z < 0)) f[k] = 'Zahl ≥ 0';
  }
  return f;
}

/** Schritte: 1 Name · 2 mindestens eine Kategorie · 3 Erreichbarkeit oder Konditionen. */
export function schritteErledigt(e: NeuerDlEntwurf): [boolean, boolean, boolean] {
  return [!!e.name.trim(), e.kategorieIds.length > 0, !!(e.telefon.trim() || e.email.trim() || e.stundensatz.trim())];
}

export function hinweis(e: NeuerDlEntwurf): { text: string; bereit: boolean } {
  if (!e.name.trim()) return { text: 'Noch offen: Name', bereit: false };
  if (e.kategorieIds.length === 0) return { text: 'Noch offen: mindestens eine Kategorie', bereit: false };
  if (Object.keys(feldFehler(e)).length > 0) return { text: 'Bitte die markierten Felder prüfen', bereit: false };
  return { text: 'Alles bereit – Dienstleister kann angelegt werden', bereit: true };
}

export interface BestandDl { id: string; name: string; aktiv: boolean; bevorzugt: boolean; vertretung: boolean; kategorieIds: readonly string[] }

/** Aktive, bevorzugte (erste Wahl) Dienstleister der gewählten Gewerke – Anlass für die Frage „Ebenfalls bevorzugt / Als Vertretung“. */
export function bevorzugteImGewerk(kategorieIds: readonly string[], bestand: readonly BestandDl[]): BestandDl[] {
  if (kategorieIds.length === 0) return [];
  return bestand.filter(b => b.aktiv && b.bevorzugt && !b.vertretung && b.kategorieIds.some(k => kategorieIds.includes(k)));
}

/** Ohne bevorzugten Dienstleister im Gewerk wird der neue bevorzugt (erste Wahl), sonst nach Wahl. */
export function bevorzugung(e: Pick<NeuerDlEntwurf, 'einordnung' | 'kategorieIds'>, bestand: readonly BestandDl[]): { ist_bevorzugt: boolean; vertretung: boolean } {
  const konflikt = bevorzugteImGewerk(e.kategorieIds, bestand).length > 0;
  if (!konflikt) return { ist_bevorzugt: true, vertretung: false };
  return { ist_bevorzugt: true, vertretung: e.einordnung === 'vertretung' };
}

const leer = (s: string) => (s.trim() ? s.trim() : null);

export function dlPayload(e: NeuerDlEntwurf, bestand: readonly BestandDl[], heute: string): Record<string, string | number | boolean | null> {
  const nz = (s: string) => { const z = zahl(s); return z === null || Number.isNaN(z) ? null : z; };
  return {
    art: e.art, name: e.name.trim(), ansprechpartner: leer(e.ansprechpartner), email: leer(e.email), telefon: leer(e.telefon),
    notdienst_telefon: leer(e.notdienstTelefon), website: leer(e.website),
    strasse: leer(e.strasse), hausnummer: leer(e.hausnummer), plz: leer(e.plz), stadt: leer(e.stadt), notizen: leer(e.notizen),
    stundensatz: nz(e.stundensatz), anfahrt: nz(e.anfahrt), notdienstzuschlag_prozent: nz(e.notdienstzuschlag),
    zahlungsziel_tage: nz(e.zahlungsziel) === null ? null : Math.round(nz(e.zahlungsziel) as number),
    iban: e.iban.trim() ? ibanNorm(e.iban) : null, ust_id: leer(e.ustId)?.toUpperCase() ?? null,
    freigabegrenze: e.freigabegrenze, beauftragung_modus: e.modus, ist_notfallkontakt: e.notfall,
    ...bevorzugung(e, bestand),
    aktiv: true, partner_seit: heute,
  };
}

/** Nachweise aus dem Formular (nur mit Datum). */
export function nachweisZeilen(e: Pick<NeuerDlEntwurf, 'freistellungBis' | 'haftpflichtBis'>): { art: 'freistellung_48b' | 'haftpflicht'; gueltig_bis: string }[] {
  return [
    ...(e.freistellungBis ? [{ art: 'freistellung_48b' as const, gueltig_bis: e.freistellungBis }] : []),
    ...(e.haftpflichtBis ? [{ art: 'haftpflicht' as const, gueltig_bis: e.haftpflichtBis }] : []),
  ];
}
